import React, { useState } from 'react';
import VaultSidebar from '../../components/vault/VaultSidebar';
import VaultHeader from '../../components/vault/VaultHeader';
import { useAppSelector, useAppDispatch } from '../../hooks/useRedux';
import { logout } from '../../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import ProfileSwitcher from '../../components/profile/ProfileSwitcher';
import AddProfileModal from '../../components/profile/AddProfileModal';
import { toast } from 'react-hot-toast';
import ExportModal from '../../components/export/ExportModal';
import { useTheme, ThemeType } from '../../contexts/ThemeContext';
import RestartTourButton from '../../components/onboarding/RestartTourButton';
import {
  UserIcon,
  ShieldCheckIcon,
  ArrowRightOnRectangleIcon,
  ChevronRightIcon,
  PaintBrushIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  PlusIcon,
  UserCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { hasPermission } from '../../utils/permissionUtils';

const SettingsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, permissions } = useAppSelector((state) => state.auth);
  const [showProfileSwitcher, setShowProfileSwitcher] = useState(false);
  const [showAddProfileModal, setShowAddProfileModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const { theme, setTheme } = useTheme();

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      dispatch(logout());
      navigate('/login');
      toast.success('Logged out successfully');
    }
  };


  const handleAddProfile = () => {
    setShowProfileSwitcher(false);
    setShowAddProfileModal(true);
  };

  const handleProfileAdded = () => {
    toast.success('Profile added successfully');
    // Refresh the data
    // This could be a call to your API to get updated profiles
  };

  const handleThemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTheme(e.target.value as ThemeType);
  };

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <VaultSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <VaultHeader />
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          {/* Hero Banner Section */}
          <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-48 bg-white rounded-full -translate-y-24 translate-x-48"></div>
              <div className="absolute bottom-0 left-0 w-72 h-36 bg-white rounded-full translate-y-18 -translate-x-36"></div>
              <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white rounded-full"></div>
              <div className="absolute top-1/3 right-1/3 w-24 h-24 bg-white rounded-full"></div>
            </div>

            <div className="relative px-6 py-12">
              <div className="max-w-7xl mx-auto">
                {/* Header Content */}
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-8">
                  <div className="mb-6 lg:mb-0">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                        <UserIcon className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-2 leading-tight">
                          Settings
                        </h1>
                        <div className="flex items-center space-x-2 text-white/80">
                          <ShieldCheckIcon className="h-5 w-5" />
                          <span className="text-sm">Account & Security Center</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xl text-white/90 max-w-2xl leading-relaxed">
                      Manage your account settings, security preferences, and customize your experience.
                      Keep your data safe and your workflow optimized.
                    </p>
                  </div>

                  {/* User Info Card */}
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border border-white/30 min-w-[300px]">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-white/20 rounded-full">
                        <UserCircleIcon className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{user?.email}</h3>
                        <p className="text-white/80 text-sm">
                          {user?.two_factor_enabled ? '🛡️ MFA Enabled' : '⚠️ MFA Disabled'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="container mx-auto px-6 py-8">
            <div className="space-y-6">

            {/* Account Section */}
            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border-2 border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-300">
              <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-6 py-4">
                <h2 className="text-lg font-semibold text-white flex items-center">
                  <UserIcon className="h-6 w-6 mr-3" />
                  Account Management
                </h2>
                <p className="text-blue-100 text-sm mt-1">Manage your account details and authentication</p>
              </div>

              <div className="px-6 py-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      <UserCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Email Address</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{user?.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all duration-200 hover:scale-105"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                    Log Out
                  </button>
                </div>
              </div>
            </div>

            {/* Security Section */}
            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border-2 border-gray-300 dark:border-gray-600 hover:border-green-400 dark:hover:border-green-500 transition-all duration-300">
              <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-6 py-4">
                <h2 className="text-lg font-semibold text-white flex items-center">
                  <ShieldCheckIcon className="h-6 w-6 mr-3" />
                  Security & Privacy
                </h2>
                <p className="text-green-100 text-sm mt-1">Protect your account with advanced security features</p>
              </div>

              <div className="px-6 py-6">
                <button
                  onClick={() => navigate('/mfa-setup')}
                  className="w-full flex justify-between items-center group p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg ${user?.two_factor_enabled ? 'bg-green-100 dark:bg-green-900' : 'bg-orange-100 dark:bg-orange-900'}`}>
                      <ShieldCheckIcon className={`h-6 w-6 ${user?.two_factor_enabled ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`} />
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-blue-500 dark:group-hover:text-blue-400">
                        Multi-factor Authentication (MFA)
                      </span>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user?.two_factor_enabled 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                            : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                        }`}>
                          {user?.two_factor_enabled ? '🛡️ Enabled' : '⚠️ Disabled'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400" />
                </button>
              </div>
            </div>

            {/* Import and Export Section */}
            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border-2 border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500 transition-all duration-300">
              <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-6 py-4">
                <h2 className="text-lg font-semibold text-white flex items-center">
                  <ArrowDownTrayIcon className="h-6 w-6 mr-3" />
                  Data Management
                </h2>
                <p className="text-purple-100 text-sm mt-1">Import and export your vault data securely</p>
              </div>

              <div className="px-6 py-6 space-y-4">
                <button
                  onClick={() => navigate('/import')}
                  className="w-full flex justify-between items-center group p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                      <ArrowUpTrayIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-purple-500 dark:group-hover:text-purple-400">
                        Import Items
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Transfer passwords from browsers and other password managers
                      </p>
                    </div>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-purple-500 dark:group-hover:text-purple-400" />
                </button>

                {hasPermission(permissions, 'vault', 'export_items') && (
                  <button
                    onClick={() => setShowExportModal(true)}
                    className="w-full flex justify-between items-center group p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-pink-100 dark:bg-pink-900 rounded-lg">
                        <ArrowDownTrayIcon className="h-6 w-6 text-pink-600 dark:text-pink-400" />
                      </div>
                      <div className="text-left">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-pink-500 dark:group-hover:text-pink-400">
                          Export Items
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Download passwords and other items as a CSV file
                        </p>
                      </div>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-pink-500 dark:group-hover:text-pink-400" />
                  </button>
                )}
              </div>
            </div>

            {/* Appearance Section */}
            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border-2 border-gray-300 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500 transition-all duration-300">
              <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-6 py-4">
                <h2 className="text-lg font-semibold text-white flex items-center">
                  <PaintBrushIcon className="h-6 w-6 mr-3" />
                  Appearance & Display
                </h2>
                <p className="text-indigo-100 text-sm mt-1">Customize your visual experience</p>
              </div>

              <div className="px-6 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                      <PaintBrushIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Theme Selection</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        Choose your preferred appearance mode
                      </p>
                    </div>
                  </div>
                  <div className="w-48">
                    <select
                      value={theme}
                      onChange={handleThemeChange}
                      className="block w-full bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-lg py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 dark:text-white transition-all duration-200"
                    >
                      <option value="light">☀️ Light Mode</option>
                      <option value="dark">🌙 Dark Mode</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Help Section */}
            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border-2 border-gray-300 dark:border-gray-600 hover:border-teal-400 dark:hover:border-teal-500 transition-all duration-300">
              <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-6 py-4">
                <h2 className="text-lg font-semibold text-white flex items-center">
                  <QuestionMarkCircleIcon className="h-6 w-6 mr-3" />
                  Help & Support
                </h2>
                <p className="text-teal-100 text-sm mt-1">Get assistance and learn about features</p>
              </div>

              <div className="px-6 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-teal-100 dark:bg-teal-900 rounded-lg">
                      <QuestionMarkCircleIcon className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Onboarding Tour</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        Restart the interactive tour to explore all features
                      </p>
                    </div>
                  </div>
                  <RestartTourButton />
                </div>
              </div>
            </div>
          </div>
        </div>
        </main>
      </div>

      {/* Profile Switcher Modal */}
      {showProfileSwitcher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Switch Account</h3>
              <button
                onClick={() => setShowProfileSwitcher(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              <ProfileSwitcher
                currentUserId={user?.id || ''}
                onAddProfile={handleAddProfile}
                onClose={() => setShowProfileSwitcher(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Profile Modal */}
      <AddProfileModal
        isOpen={showAddProfileModal}
        onClose={() => setShowAddProfileModal(false)}
        onSuccess={handleProfileAdded}
      />

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} />
      )}
    </div>
  );
};

export default SettingsPage; 